import { useCallback, useEffect, useRef, useState } from 'react';
import { useRadarLocale } from '@/i18n/RadarLocaleContext';
import { loadSdk } from './RadarGmailDraft';
import { radarApi, type EmailSignalsState } from './radarApi';

const clientId=import.meta.env.VITE_RADAR_GOOGLE_CLIENT_ID as string|undefined;
const readonlyScope='https://www.googleapis.com/auth/gmail.readonly';
const labels={pending:'Awaiting check',observed:'Open signal observed',none:'No open signal yet',unavailable:'Tracking unavailable'};

export function RadarEmailSignals({token,workspaceId,enabled,active=true,onState,compact=false}:{
  token:string;workspaceId:string;enabled:boolean;active?:boolean;onState?:(data:EmailSignalsState)=>void;compact?:boolean;
}) {
  const {t,e}=useRadarLocale();
  const [data,setData]=useState<EmailSignalsState|null>(null);
  const [busy,setBusy]=useState(false),[error,setError]=useState('');
  const authorization=useRef<{token:string;expiresAt:number}|null>(null);
  const revision=useRef(0),alive=useRef(false),lock=useRef(false),lastSync=useRef(0);
  const read=useCallback(async(force=false)=>{
    if(!enabled || !active || lock.current)return;
    const run=++revision.current;lock.current=true;setBusy(true);setError('');
    try {
      let next=await radarApi.emailSignals(token,workspaceId);
      const auth=authorization.current;
      if(alive.current && revision.current===run && next.enabled && auth && auth.expiresAt>Date.now()
          && (force || Date.now()-lastSync.current>60000)) {
        lastSync.current=Date.now();next=await radarApi.refreshEmailSignals(token,workspaceId,auth.token);
      }
      if(alive.current && revision.current===run)setData(next);
    }catch(err){if(alive.current && revision.current===run)setError(err instanceof Error?err.message:'Email signals could not refresh.');}
    finally{if(alive.current && revision.current===run){lock.current=false;setBusy(false);}}
  },[token,workspaceId,enabled,active]);

  useEffect(()=>{
    authorization.current=null;lastSync.current=0;setData(null);
  },[token,workspaceId]);
  useEffect(()=>{
    alive.current=true;lock.current=false;
    if(enabled && active)void read();
    return()=>{alive.current=false;revision.current++;};
  },[read,enabled,active]);

  async function refresh() {
    if(lock.current)return;
    if(authorization.current && authorization.current.expiresAt>Date.now()){await read(true);return;}
    if(!clientId){await read();return;}
    const run=++revision.current;lock.current=true;setBusy(true);setError('');
    const finish=()=>{if(alive.current && revision.current===run){lock.current=false;setBusy(false);}};
    try {
      const sdk=await loadSdk();
      if(!alive.current || revision.current!==run)return;
      sdk.accounts.oauth2.initTokenClient({client_id:clientId,scope:'openid email '+readonlyScope,
        error_callback:()=>{if(alive.current && revision.current===run)setError('Gmail authorization was cancelled.');finish();},
        callback:response=>{
          if(!alive.current || revision.current!==run)return;
          if(response.error || !response.access_token || !response.scope?.split(' ').includes(readonlyScope)){
            setError('Gmail read permission is required to check sending. Draft export is unchanged.');finish();return;
          }
          authorization.current={token:response.access_token,expiresAt:Date.now()+((response.expires_in||3600)-60)*1000};
          finish();void read(true);
        }}).requestAccessToken({prompt:'select_account'});
    }catch(err){if(alive.current && revision.current===run)setError(err instanceof Error?err.message:'Gmail authorization is unavailable.');finish();}
  }

  useEffect(()=>{if(data)onState?.(data);},[data,onState]);

  if(!enabled || !data?.enabled)return error && enabled?<p role="alert">{e(error)}</p>:null;
  return <section className="radar-act-gmail" aria-label={t('Email open signals')}>
    <h3>{t('Email open signals')}</h3>
    <p className="radar-muted">{t('New Gmail exports include an image to record open signals. Your own Gmail views and automatic image loading may also trigger a signal; no signal does not mean unread.')}</p>
    <button className="radar-secondary-action" disabled={busy} onClick={()=>void refresh()}>{t(busy?'Checking…':'Refresh')}</button>
    {!clientId && <p className="radar-muted">{t('Gmail connection is not configured yet.')}</p>}
    {error && <p role="alert">{e(error)}</p>}
    {compact&&data.items.some(item=>item.sync_error==='reconnect')&&<p role="status">{t('Reconnect Gmail to refresh email signals.')}</p>}
    {compact&&data.items.some(item=>item.sync_error==='check_failed')&&<p role="status">{t('Could not check sending. Try refreshing again.')}</p>}
    {!compact&&<div className="radar-act-history">{data.items.map(item=><article key={item.id}>
      <strong>{item.subject}</strong><small>{item.recipient} · {item.account_email}</small>
      <p>{t(labels[item.status])}{item.signal_at && <> · <time dateTime={item.signal_at}>{new Date(item.signal_at).toLocaleString()}</time></>}</p>
      {item.last_checked_at && <small>{t('Last checked')} · {new Date(item.last_checked_at).toLocaleString()}</small>}
      {item.sync_error==='reconnect' && <small>{t('Reconnect Gmail to refresh email signals.')}</small>}
      {item.sync_error==='check_failed' && <small>{t('Could not check sending. Try refreshing again.')}</small>}
    </article>)}</div>}
  </section>;
}
